import * as React from 'react'
import * as Redux from 'react-redux'
import styled from 'styled-components'

import * as api from '@owl-nest/api-client/latest'
import { t, tn, tc } from '@owl-nest/localize'
import { MoneyDisplayer } from '@owl-nest/money'
import * as models from '@owl-nest/models'
import { text } from '@owl-nest/utils'
import * as env from '@owl-nest/config'

import { LocaleMoneyDisplayer } from '@ulule/owl-kit-components/next'
import * as plume from '@ulule/owl-kit-components/next'

import { DisplayMode } from '../../../../context'
import * as projectModel from '../../../../models/project'
import { RootState } from '../../../../../reducers'

import { EditButton } from '../EditButton'
import { Triptych } from './Triptych'
import { AnimateCounter } from '../AnimateCounter'
import { MIDDLE_TABLET_LAPTOP_BREAKPOINT } from '../../constants'

type ProgressProps = {
  percent: number
  amountRaised: number
  committed: number
  goal: number
}

export function Progress({ percent, amountRaised, committed, goal }: ProgressProps): React.ReactElement<ProgressProps> {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const displayMode = React.useContext(DisplayMode)

  const isPresale = project.type === 'presale'
  const isDonationBased = models.project.isDonationBased(project)
  const isEditMode = displayMode === 'edit'
  const isFinishedAndFunded = models.project.success<projectModel.ExtraFields>(project)

  return (
    <ProgressView $isEditMode={isEditMode}>
      {!isDonationBased && isFinishedAndFunded && (
        <FinishedAndFundedLabel>{t('Successfully funded!')}</FinishedAndFundedLabel>
      )}
      <Triptych
        description={
          goal ? (
            <ProgressDescription goal={goal} percent={percent} project={project} amountRaised={amountRaised} />
          ) : null
        }
        leftZone={
          isDonationBased ? (
            <plume.illustrations.twoToned.EggCoin size={54} noBackground />
          ) : isFinishedAndFunded ? (
            <plume.glyphs.twoToned.EggFunded size={52} />
          ) : (
            <plume.glyphs.crowdfunding.ProgressEgg progress={percent} size={52} />
          )
        }
        title={
          <GoalWrapper>
            <plume.styles.heading.L>
              {project.is_online ? (
                isPresale ? (
                  <AnimateCounter id={committed}>{text.numberToLocaleString(committed)}</AnimateCounter>
                ) : (
                  <AnimateCounter id={amountRaised}>
                    <MoneyDisplayer amount={amountRaised} project={project} />
                  </AnimateCounter>
                )
              ) : (
                '—'
              )}
            </plume.styles.heading.L>
            {goal > 0 && (
              <GoalSubtitle>
                {isPresale
                  ? tn(
                      'presale out of %(goal)s',
                      'presales out of %(goal)s',
                      text.numberToLocaleString(project.committed),
                      {
                        goal: text.numberToLocaleString(goal),
                      },
                    )
                  : tc('out of [goal]', {
                      goal: <MoneyDisplayer amount={goal} project={project} />,
                    })}
              </GoalSubtitle>
            )}
          </GoalWrapper>
        }
      />
      {isEditMode &&
        !models.project.refused<projectModel.ExtraFields>(project) &&
        project.user_role === api.UserRole.Owner && (
          <EditButtonContainer>
            <EditButton link={`/projects/${project.id}/backoffice/goal`} />
          </EditButtonContainer>
        )}
    </ProgressView>
  )
}

type ProgressDescriptionProps = {
  goal: number
  percent: number
  precision?: number
  project: projectModel.Project
  amountRaised: number
}

function ProgressDescription({
  goal,
  percent,
  precision,
  project,
  amountRaised,
}: ProgressDescriptionProps): React.ReactElement<ProgressDescriptionProps> {
  const userCurrency = env.getUserConfig().currency
  const userLocale = env.getUserConfig().lang
  const conversionNeeded = userCurrency !== project.currency
  const ConversionIcon = getCurrencyIcon(userCurrency)
  const isPresale = project.type === 'presale'

  return (
    <>
      <plume.Tag color={project.goal_raised ? 'green' : 'blue'} size="small">
        <AnimateCounter id={percent}>
          {t('Funded at %(percentFunded)s', { percentFunded: `${text.numberToLocaleString(percent)} %` })}{' '}
        </AnimateCounter>
      </plume.Tag>
      {conversionNeeded && !isPresale && env.CURRENCIES_RATES && (
        <>
          <ConversionWrapper>
            <ConversionIcon size={14} />~
            {tc('[amountRaised] out of [goal]', {
              amountRaised: (
                <LocaleMoneyDisplayer
                  rates={env.CURRENCIES_RATES}
                  amount={amountRaised}
                  currency={project.currency}
                  targetCurrency={userCurrency}
                  localeFormat={userLocale}
                />
              ),
              goal: (
                <LocaleMoneyDisplayer
                  rates={env.CURRENCIES_RATES}
                  amount={goal}
                  currency={project.currency}
                  targetCurrency={userCurrency}
                  localeFormat={userLocale}
                  precision={precision}
                />
              ),
            })}
          </ConversionWrapper>
        </>
      )}
    </>
  )
}

function getCurrencyIcon(currency: string | undefined): React.ComponentType<any> {
  let iconName = 'Dolls'

  if (currency === 'EUR') {
    iconName = 'Eur'
  } else if (currency === 'CHF') {
    iconName = 'Chf'
  } else if (currency === 'DKK' || currency === 'NOK' || currency === 'SEK') {
    iconName = 'Kr'
  } else if (currency === 'GBP') {
    iconName = 'Pound'
  }

  return (plume.glyphs as any)['currencies'][`${iconName}`]
}

const EditButtonContainer = styled.span`
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    top: -15px;
  }
`

const ProgressView = styled.div<{ $isEditMode: boolean }>`
  position: relative;

  ${plume.glyphs.crowdfunding.ProgressEgg} {
    margin-right: 5px;
    width: 40px;
  }

  ${plume.styles.tag.RoleTag} {
    margin-right: 6px;
    margin-top: 6px;
    line-height: 19px;
  }

  @media screen and ${({ $isEditMode }) => ($isEditMode ? plume.BREAKPOINTS.LAPTOP : MIDDLE_TABLET_LAPTOP_BREAKPOINT)} {
    margin-top: 0;
  }
`

const GoalWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`

const ConversionWrapper = styled.span`
  color: ${plume.COLORS.TEXT_TERTIARY};
  display: inline-block;
  font-size: 11px;
  line-height: 16px;
  vertical-align: middle;
  margin-left: 8px;

  ${plume.glyphs.Icon} {
    color: ${plume.COLORS.PRIMARY_GREY_500};
    margin-right: 6px;
  }
`

const FinishedAndFundedLabel = styled(plume.styles.heading.S)`
  padding-bottom: 20px;
`

const GoalSubtitle = styled(plume.styles.copy.S.regular)`
  color: ${plume.COLORS.PRIMARY_SAND_900};
`
